import {
  FloatingFocusManager,
  FloatingNode,
  FloatingPortal,
  useFloating,
  useFloatingNodeId,
  useTransitionStyles,
} from '@floating-ui/react'
import { faTimes } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'
import { IPopover } from '../Popover/hooks/usePopper'
import { IHoverPopover } from '../Tooltip/hooks/useHoverPopper'

interface DrawerValue<T extends HTMLElement>
  extends Omit<IPopover<T>, 'target' | 'targetProps'>,
    Partial<Pick<IHoverPopover<T>, 'isInstantPhase' | 'delayId'>> {}

export interface IDrawerProps<T extends HTMLElement> extends DrawerValue<T> {
  bodyProps?: React.HTMLAttributes<HTMLDivElement>
  className?: string
  style?: React.CSSProperties
  actionButton?: React.ReactNode
  maxWidth?: string
}

export const Drawer = <T extends HTMLElement>({
  actionButton,
  isOpen,
  toggle: handleClose,
  maxWidth = '25rem',
  context,
  children,
  refs,
  floatingStyles,
  getFloatingProps,
  bodyProps,
  isInstantPhase,
  delayId,
  ...props
}: React.PropsWithChildren<IDrawerProps<T>>) => {
  const nodeId = useFloatingNodeId()
  useFloating({
    nodeId,
  })
  const instantDuration = 0
  const duration = 600
  const { isMounted, styles } = useTransitionStyles(context, {
    duration:
      isInstantPhase && isOpen
        ? {
            open: instantDuration,
            close: delayId === nodeId ? duration : instantDuration,
          }
        : duration,
    initial: {
      transform: 'translate(100%)',
    },
    open: {
      transform: 'translateX(0)',
    },
    close: {
      transform: 'translateX(100%)',
    },
  })
  if (!isMounted) return null
  return (
    <FloatingFocusManager context={context} initialFocus={-1} closeOnFocusOut={false}>
      <FloatingNode id={nodeId}>
        <FloatingPortal id={nodeId}>
          <div
            ref={refs.setFloating}
            style={{
              ...props.style,
              ...floatingStyles,
              ...styles,
              position: 'fixed',
              maxWidth,
            }}
            {...getFloatingProps()}
            className={['drawer shadow-sm', props.className].filter(Boolean).join(' ')}
          >
            <div className="p-4 mx-2">
              <div className="text-end">
                {actionButton}
                <button
                  type="button"
                  aria-label="Close"
                  className="btn btn-link text-body m-0"
                  onClick={handleClose}
                >
                  <span className="visually-hidden">Close</span>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              <div>{children}</div>
            </div>
          </div>
          {isMounted && (
            <div className={`drawer-backdrop fade ${isOpen ? 'show fade-in' : 'fade-out'}`} />
          )}
        </FloatingPortal>
      </FloatingNode>
    </FloatingFocusManager>
  )
}
